import { useCallback, useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { styled } from 'styled-components';

import Spinner from './shared/Spinner';
import { useAppContext } from '../contextApi/context';
import { getMovementDetails, createMovement } from '../services/StockMovementService';
import { ToggleOption, ModalButton } from '../styles/StyledComponents';

const MovementAnnulmentModal = ({ closeHandler, actionHandler, movementId, show }) => {
    // ----- Context
    const { loggedUser } = useAppContext();

    // ----- State
    let [movementDetails, setMovementDetails] = useState(null);
    let [description, setDescription] = useState('');
    let [errorDescription, setErrorDescription] = useState(false);
    let [allGood, setAllGood] = useState(false);
    let [isComplete, setIsComplete] = useState(false);
    let [showStockError, setShowStockError] = useState(false);
    let [invoice, setInvoice] = useState(null);

    // ----- API Fetch Actions
    const fetchStockMovementDetails = useCallback(async () => {
        return await getMovementDetails(loggedUser.token, movementId);
    }, [loggedUser.token, movementId]);

    // ----- Hooks
    useEffect(() => {
        fetchStockMovementDetails().then((data) => {
            setInvoice(data.invoice);

            let details = data.details.map((item) => {
                return {
                    articleProviderId: item.articleProviderId,
                    articleName: item.articleName,
                    articleId: item.articleId,
                    brand: item.brand,
                    amount: item.amount,
                    amountToAnnul: 0,
                    showError: false,
                    isService: item.isService,
                    serviceConcept: item.serviceConcept,
                    providerId: item.providerId,
                };
            });
            setMovementDetails(details);
        });
    }, [fetchStockMovementDetails]);

    const validateAmounts = (details, isCompleteMode) => {
        // Check for validation errors (empty values or amounts greater than available)
        const hasValidationErrors = details.some((item) => item.showError);

        // Check if all amounts are zero when in partial mode
        const allAmountsZero = !isCompleteMode && details.every((item) => Number(item.amountToAnnul) === 0 || item.amountToAnnul === '');

        return !hasValidationErrors && !allAmountsZero;
    };

    const handleApiErrors = (errorMessages) => {
        if (!errorMessages || !Array.isArray(errorMessages)) return;

        // Parse each error to extract articleId and brand
        const errorPairs = errorMessages.map((errorMsg) => {
            const articleMatch = errorMsg.match(/article\s+(\d+)/);
            const brandMatch = errorMsg.match(/brand\s+(\w+)/);

            return {
                articleId: articleMatch ? articleMatch[1] : null,
                brand: brandMatch ? brandMatch[1] : null,
            };
        });

        // Update movementDetails to show errors for matching items
        setMovementDetails((prevDetails) => {
            const updatedDetails = prevDetails.map((item) => {
                if (!item.isService) {
                    const hasError = errorPairs.some((error) => {
                        // Match by articleId and brand
                        return error.articleId === String(item.articleId) && error.brand === item.brand;
                    });

                    return {
                        ...item,
                        showError: hasError || item.showError,
                    };
                } else {
                    // Services don't get errors
                    return item;
                }
            });

            setShowStockError(true);
            setAllGood(validateAmounts(updatedDetails, isComplete));

            return updatedDetails;
        });
    };

    // ----- Actions
    const handleClose = () => {
        setMovementDetails(null);
        closeHandler();
    };

    const handleAction = async () => {
        setErrorDescription(false);
        setShowStockError(false);

        if (description === '') {
            setErrorDescription(true);
        } else {
            let details = movementDetails.map((item) => {
                return {
                    articleProviderId: !item.isService ? item.articleProviderId : null,
                    amount: isComplete ? item.amount : Number(item.amountToAnnul),
                    brand: !isComplete && !item.isService ? item.brand : null,
                    serviceConcept: item.isService ? item.serviceConcept : null,
                    providerId: item.isService ? item.providerId : null,
                    isService: item.isService,
                    articleId: item.articleId,
                };
            });

            const payload = {
                type: 'ANNULMENT',
                annulmentType: isComplete ? 'COMPLETE' : 'PARTIAL',
                reversedMovement: movementId,
                details,
                description: description,
            };

            const response = await createMovement(loggedUser.token, payload);

            if (response.status === 201) {
                actionHandler();
            } else {
                // Error from API
                const { data } = response;

                if (data && Array.isArray(data) && data.length > 0) {
                    handleApiErrors(data);
                }
            }
        }
    };

    const handleInputChange = (detailIndex, e) => {
        let amountToAnnul = e.target.value;

        let newMovementDetails = movementDetails.map((item, index) => {
            if (index === detailIndex) {
                // Update the amountToAnnul value
                item.amountToAnnul = amountToAnnul;

                // Check for errors
                if (amountToAnnul === '' || Number(amountToAnnul) > item.amount) {
                    item.showError = true;
                } else {
                    item.showError = false;
                }
            }
            return item;
        });

        setMovementDetails(newMovementDetails);
        setAllGood(validateAmounts(newMovementDetails, isComplete));
    };

    const handleKeyDown = (e) => {
        const acceptedKeys = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '.', 'ArrowLeft', 'ArrowRight', 'Backspace', 'Delete'];

        if (!acceptedKeys.includes(e.key)) {
            e.preventDefault();
        }
    };

    const handleAnnulmentTypeChange = (e) => {
        const isCompleteType = e.target.value === 'true';
        setIsComplete(isCompleteType);

        // Re-validate all inputs when switching back to partial mode
        if (movementDetails) {
            const updatedDetails = movementDetails.map((item) => {
                // Re-check for errors based on current values
                const hasError = !isCompleteType && (item.amountToAnnul === '' || Number(item.amountToAnnul) > item.amount);

                return {
                    ...item,
                    showError: hasError,
                };
            });

            setMovementDetails(updatedDetails);
            setAllGood(validateAmounts(updatedDetails, isCompleteType));
        }
    };

    // ----- Render
    return (
        <Modal show={show} size='lg' backdrop='static' centered onHide={handleClose}>
            {movementDetails ? (
                <>
                    <Modal.Header className='fw-bold' closeButton>
                        Anulación - Factura #{invoice}
                    </Modal.Header>
                    <Modal.Body>
                        <div className='d-flex flex-column'>
                            <label className='fw-bold mb-1' htmlFor='description'>
                                Descripción
                            </label>
                            <textarea
                                className='form-control'
                                id='description'
                                onChange={(e) => setDescription(e.target.value)}
                                maxLength={100}
                                rows='3'
                                style={{ resize: 'none' }}
                            />
                            {errorDescription && (
                                <Error className='mt-1' role='alert'>
                                    Campo requerido
                                </Error>
                            )}
                            <hr />

                            <ToggleOption className='d-flex justify-content-center mb-3'>
                                <input
                                    type='radio'
                                    className='btn-check'
                                    name='annulmentType'
                                    id='partialType'
                                    value='false'
                                    onChange={handleAnnulmentTypeChange}
                                    checked={!isComplete}
                                />
                                <label className='btn btn-outline-success first-option' htmlFor='partialType'>
                                    Parcial
                                </label>

                                <input
                                    type='radio'
                                    className='btn-check'
                                    name='annulmentType'
                                    id='completeType'
                                    value='true'
                                    onChange={handleAnnulmentTypeChange}
                                    checked={isComplete}
                                />
                                <label className='btn btn-outline-success second-option' htmlFor='completeType'>
                                    Completa
                                </label>
                            </ToggleOption>

                            <label className='fw-bold'>Detalles</label>
                            <p className='mb-1 ms-2'>
                                {isComplete
                                    ? 'Nota: La compra completa será anulada (Esta acción es irreversible).'
                                    : 'Nota: Las anulaciones no pueden exceder la cantidad original y debe anular al menos un artículo.'}
                            </p>

                            {showStockError && <Error className='ms-2 '>* Inventario insuficiente para los artículos marcados con X.</Error>}

                            <table className='table table-bordered my-2'>
                                <thead>
                                    <tr>
                                        <th scope='col'>Concepto</th>
                                        <th scope='col'>Cantidad</th>
                                        <th scope='col'>Anulación</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {movementDetails.map((item, index) => (
                                        <tr key={index}>
                                            <td>{item.isService ? item.serviceConcept : `${item.articleName} (${item.brand})`}</td>
                                            <td>{item.amount}</td>
                                            <ReturnColumn>
                                                <input
                                                    type='text'
                                                    value={isComplete ? item.amount : item.amountToAnnul}
                                                    maxLength={10}
                                                    disabled={isComplete}
                                                    onKeyDown={!isComplete ? handleKeyDown : undefined}
                                                    onChange={!isComplete ? (e) => handleInputChange(index, e) : undefined}
                                                />
                                                <span className='ms-2'>{item.showError && 'x'}</span>
                                            </ReturnColumn>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <ModalButton className='cancel' onClick={handleClose}>
                            Cancelar
                        </ModalButton>
                        <ModalButton className='action' onClick={handleAction} disabled={!allGood}>
                            Aceptar
                        </ModalButton>
                    </Modal.Footer>
                </>
            ) : (
                <Spinner />
            )}
        </Modal>
    );
};

const Error = styled.span`
    color: #d04437;
    font-size: 14px;
`;

const ReturnColumn = styled.td`
    align-items: center;
    display: flex;
    flex-direction: row;

    > input {
        width: 70px;
    }

    > span {
        color: #d04437;
        font-size: 16px;
        font-weight: bold;
        margin-left: 0.5rem;
    }
`;

export default MovementAnnulmentModal;
